import { fetchApi, getFetchApiJSONResponse, catchFetchApiError } from 'shared/functions';
import { getFetchApiTextResponse } from 'shared/functions/fetchApi/fetchApi';
import qs from 'qs';
import { Event, EventCategory } from '../types/eventsTypes';

export const fetchEvents = (
  category: EventCategory | '',
  timeline: string,
  sortDesc: boolean,
  status: 'ACTIVE' | 'DRAFT' | 'ALL' = 'ACTIVE',
  favourites: boolean,
) => {
  const params = qs.stringify(
    {
      page: 1,
      limit: 100,
      sort: 'eventDate',
      dir: sortDesc ? 'ASC' : 'DESC',
      categories: category || null,
      futureEvents: timeline === 'upcoming' || null,
      pastEvents: timeline === 'past' || null,
      status: status === 'ALL' ? null : status,
      favourites: favourites || null,
    },
    { skipNulls: true },
  );

  return fetchApi(`/storefront/livestorm/events?${params}`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const fetchEventDetails = (id: string) =>
  fetchApi(`/storefront/livestorm/events/${id}`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);

export const fetchEventTitles = (title: string) =>
  fetchApi(`/storefront/livestorm/eventtitles?pageNumber=0&title=${title}`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);

export const fetchEventSessions = (id: string) =>
  fetchApi(`/storefront/livestorm/integration/events/${id}/sessions`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);

export const fetchSessionDetails = (id: string) =>
  fetchApi(`/storefront/livestorm/integration/sessions/${id}`)
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);

export const uploadDocument = (file: File) => {
  const formData = new FormData();

  formData.append('data', JSON.stringify({ title: file.name, documentType: 'LIVESTORM_EVENT' }));
  formData.append('file', file);

  return fetchApi(
    `/storefront/document_upload`,
    {
      method: 'POST',
      body: formData,
    },
    ['Content-Type'],
  )
    .then(getFetchApiTextResponse)
    .catch(catchFetchApiError);
};

export const saveEvent = async (event: Event) => {
  const formData = new FormData();
  const { image, ...data } = event;

  /* Upload documents (if there are any) and save their ids */
  const documents = event.documentList
    ? {
        documentList: (
          await Promise.all((event.documentList as File[]).map(uploadDocument))
        ).map(id => ({ id })),
      }
    : {};

  formData.append('data', JSON.stringify({ ...data, ...documents }));
  formData.append('image', image);

  return fetchApi(
    `/storefront/livestorm/events`,
    {
      method: 'POST',
      body: formData,
    },
    ['Content-Type'],
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const updateEvent = async (event: Event) => {
  const formData = new FormData();
  const { image, ...data } = event;

  formData.append('data', JSON.stringify(data));
  formData.append('image', image);

  return fetchApi(
    `/storefront/livestorm/events/${event.id}`,
    {
      method: 'PUT',
      body: formData,
    },
    ['Content-Type'],
  )
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);
};

export const activateEvent = (id: string) =>
  fetchApi(`/storefront/livestorm/events/${id}/status/ACTIVE`, {
    method: 'PUT',
  })
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);

export const deactivateEvent = (id: string) =>
  fetchApi(`/storefront/livestorm/events/${id}/status/DRAFT`, {
    method: 'PUT',
  })
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);

export const registerForAnEvent = (id: string) =>
  fetchApi(`/storefront/livestorm/events/${id}/register`, {
    method: 'POST',
  }).catch(catchFetchApiError);

export const unregisterFromAnEvent = (id: string) =>
  fetchApi(`/storefront/livestorm/events/${id}/unregister`, {
    method: 'DELETE',
  }).catch(catchFetchApiError);

export const fetchEventsProperties = () =>
  fetchApi('/shop/dictionaries/')
    .then(getFetchApiJSONResponse)
    .catch(catchFetchApiError);

export const addEventToFavourites = (id: string) =>
  fetchApi(`/storefront/livestorm/events/fav/${id}`, {
    method: 'PUT',
  }).catch(catchFetchApiError);

export const removeEventFromFavourites = (id: string) =>
  fetchApi(`/storefront/livestorm/events/fav/${id}`, {
    method: 'DELETE',
  }).catch(catchFetchApiError);
